import React, { useEffect, useState } from 'react';
import { Box, Grid, Paper, Typography, CircularProgress, LinearProgress, IconButton } from '@mui/material';
import { Movie, Timer, RateReview, BookmarkBorder, Grade, Launch } from '@mui/icons-material';
import { UserProfile } from '../../../types/UserProfile';
import { getMovieProfileStats } from '../../../services/movieApi';
import { useNavigate } from 'react-router-dom';
import FavoriteMovieWidget from './FavoriteMovieWidget';
import RecentMovieWidget from './RecentMovieWidget';

interface MovieProfileStats {
  watched_in_current_year: number;
  review_count: number;
  last_updated?: string;
  rating_stats: {
    average: number | null;
    count: number;
    distribution: { [key: string]: number };
    last_updated?: string;
  };
}

interface MovieProfileTabProps {
  profile: UserProfile | null;
  isPublic?: boolean;
}

const commonPaperStyle = {
  p: 2.5,
  height: '100%',
  padding: { xs: 1.5, sm: 2.5 },
  borderRadius: 2,
  border: '1px solid rgba(255, 255, 255, 0.1)',
  background: 'background.paper',
  backdropFilter: 'blur(10px)',
  transition: 'all 0.2s ease',
  position: 'relative',
};

const iconStyle = {
  mr: 1,
  color: 'text.primary',
  fontSize: '1rem',
  display: 'none',
};

const statsNumberStyle = {
  fontSize: { xs: '1.6rem', sm: '2rem' },
  fontWeight: 600,
  color: '#e6edf3',
  letterSpacing: '-0.5px',
  lineHeight: 1.2,
};

const subtitleStyle = { color: '#8b949e' };

const progressStyle = {
  height: 2,
  borderRadius: 2,
  backgroundColor: 'rgba(110, 118, 129, 0.1)',
  '& .MuiLinearProgress-bar': { backgroundColor: 'primary.main' },
};

const getOpacityByRank = (index: number, total: number): number => {
  return Math.max(1.0 - (index / total) * 0.7, 0.3);
};

const getRelativeTimeString = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
  if (diffInSeconds < 60) return '数秒前';
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}分前`;
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}時間前`;
  return `${Math.floor(diffInSeconds / 86400)}日前`;
};

interface StatsWidgetProps {
  title: string;
  value: number | string;
  subtitle: string;
  icon?: React.ReactElement;
  onClick?: () => void;
  lastUpdated?: string;
}

const StatsWidget: React.FC<StatsWidgetProps> = ({ title, value, subtitle, icon, onClick, lastUpdated }) => (
  <Paper elevation={0} sx={commonPaperStyle}>
    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
      {icon && React.cloneElement(icon, { sx: iconStyle })}
      <Typography variant="subtitle2" color="text.secondary">{title}</Typography>
    </Box>
    <Typography variant="h3" component="div" sx={statsNumberStyle}>{value}</Typography>
    <Typography variant="body2" color="text.secondary" sx={subtitleStyle}>{subtitle}</Typography>
    {onClick && (
      <IconButton onClick={onClick} size="small" sx={{ position: 'absolute', right: { xs: 4, sm: 8 }, bottom: { xs: 4, sm: 8 }, color: 'text.secondary', '&:hover': { color: 'primary.main' } }}>
        <Launch fontSize="small" />
      </IconButton>
    )}
    {lastUpdated && (
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'right', fontSize: '0.6rem', fontWeight: '400', opacity: 0.7 }}>
        データ更新: {getRelativeTimeString(lastUpdated)}
      </Typography>
    )}
  </Paper>
);

const MovieProfileTab: React.FC<MovieProfileTabProps> = ({ profile, isPublic }) => {
  const [stats, setStats] = useState<MovieProfileStats | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchStats = async () => {
      if (!profile) return;
      try {
        const response = await getMovieProfileStats(isPublic ? profile.username : undefined);
        setStats(response.data);
      } catch (error) {
        console.error('映画統計情報の取得に失敗しました:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, [profile, isPublic]);

  if (!profile) return null;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 0 }}>
        <FavoriteMovieWidget username={isPublic ? profile.username : undefined} />
      </Box>

      <Box sx={{ py: { xs: 1.5, sm: 2 } }}>
        <Grid container spacing={{ xs: 1.5, sm: 2 }}>
          <Grid item xs={6} sm={6} md={3}>
            <StatsWidget
              title="総視聴数"
              value={profile?.movie_status_counts?.[0] || 0}
              subtitle="movies"
              icon={<Movie />}
              onClick={() => {
                const url = isPublic ? `/profile/${profile.username}?tab=library&media=movie&status=1` : '/profile?tab=library&media=movie&status=1';
                navigate(url);
              }}
            />
          </Grid>
          <Grid item xs={6} sm={6} md={3}>
            <StatsWidget
              title={`${new Date().getFullYear()}年視聴数`}
              value={stats?.watched_in_current_year || 0}
              subtitle="movies"
              icon={<Timer />}
            />
          </Grid>
          <Grid item xs={6} sm={6} md={3}>
            <StatsWidget
              title="レビュー投稿数"
              value={stats?.review_count || 0}
              subtitle="reviews"
              icon={<RateReview />}
              onClick={() => {
                const url = isPublic ? `/profile/${profile.username}?tab=reviews&media=movie&page=1` : '/profile?tab=reviews&media=movie&page=1';
                navigate(url);
              }}
            />
          </Grid>
          <Grid item xs={6} sm={6} md={3}>
            <StatsWidget
              title="ウォッチリスト"
              value={profile?.movie_status_counts?.[4] || 0}
              subtitle="movies"
              icon={<BookmarkBorder />}
              onClick={() => {
                const url = isPublic ? `/profile/${profile.username}?tab=library&media=movie&status=5` : '/profile?tab=library&media=movie&status=5';
                navigate(url);
              }}
            />
          </Grid>
        </Grid>
      </Box>

      <Grid container spacing={{ xs: 1.5, sm: 2 }} alignItems="flex-start">
        <Grid item xs={12} md={8}>
          <RecentMovieWidget username={isPublic ? profile.username : undefined} isPublic={isPublic} />
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper elevation={0} sx={commonPaperStyle}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Grade sx={iconStyle} />
              <Typography variant="subtitle2" color="text.secondary">評価統計</Typography>
            </Box>
            <Typography variant="h3" component="div" sx={{ fontWeight: 'bold', mb: 2 }}>
              {stats?.rating_stats.average ? stats.rating_stats.average.toFixed(1) : '-'}
              <Typography component="span" sx={{ fontSize: '0.8rem', color: 'text.secondary', ml: 1 }}>Avg.</Typography>
            </Typography>
            <Box sx={{ mb: 1 }}>
              {stats?.rating_stats.distribution &&
                Object.entries(stats.rating_stats.distribution)
                  .sort(([a], [b]) => Number(b) - Number(a))
                  .map(([score, count], index, array) => {
                    const totalCount = Object.values(stats.rating_stats.distribution).reduce((a, b) => a + b, 0);
                    if (totalCount === 0) return null;
                    const percentage = (count / totalCount) * 100;
                    const opacity = getOpacityByRank(index, array.length);
                    return (
                      <Box key={score} sx={{ mb: 0.25 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0 }}>
                          <Typography variant="caption">{Number(score).toFixed(1)}</Typography>
                          <Typography variant="caption">
                            {count} <Typography component="span" variant="caption" sx={{ opacity: 0.6, ml: 0.5 }}>{percentage.toFixed(1)}%</Typography>
                          </Typography>
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={percentage}
                          sx={{ ...progressStyle, '& .MuiLinearProgress-bar': { ...progressStyle['& .MuiLinearProgress-bar'], opacity } }}
                        />
                      </Box>
                    );
                  })}
            </Box>
            <Typography variant="body2" color="text.secondary" sx={subtitleStyle}>
              {stats?.rating_stats.count && (profile?.movie_status_counts?.[0] || 0) > 0
                ? `${stats.rating_stats.count} movies (${((stats.rating_stats.count / (profile?.movie_status_counts?.[0] || 1)) * 100).toFixed(1)}%) rated`
                : '0% of movies rated'}
            </Typography>
            {!isPublic && stats?.rating_stats.last_updated && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'right', fontSize: '0.6rem', fontWeight: '400', opacity: 0.7 }}>
                データ更新: {getRelativeTimeString(stats.rating_stats.last_updated)}
              </Typography>
            )}
          </Paper>

          {isPublic && stats?.last_updated && (
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem', fontWeight: '400', opacity: 0.7, mt: 1, display: 'block' }}>
              データ更新: {getRelativeTimeString(stats.last_updated)}
            </Typography>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default MovieProfileTab;
